import React, { useMemo } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const EMPTY = '';

export function buildEmptyModule(mod) {
  return {
    moduleId: mod.id,
    moduleName: mod.name,
    remarks: '',
    sendCustomNotesOnly: false,
    selections: (mod.parameters || []).map((p) => ({ parameterId: p.id, optionId: '' })),
  };
}

export function buildAllModuleStates(flatModules, existing) {
  const byId = new Map((existing?.modules || []).map((m) => [m.moduleId, m]));
  return flatModules.map((mod) => {
    const saved = byId.get(mod.id);
    if (saved) {
      const remarks = saved.remarks || '';
      const selections = (mod.parameters || []).map((p) => {
        const prev = saved.selections?.find((s) => s.parameterId === p.id);
        return { parameterId: p.id, optionId: prev?.optionId || '' };
      });
      return {
        moduleId: mod.id,
        moduleName: mod.name,
        remarks,
        sendCustomNotesOnly: Boolean(saved.sendCustomNotesOnly) && Boolean(remarks.trim()),
        selections,
      };
    }
    return buildEmptyModule(mod);
  });
}

export function moduleHasData(modState) {
  if (modState.remarks?.trim()) return true;
  return modState.selections.some((s) => s.optionId);
}

export function modulesForSave(allModules) {
  return allModules
    .filter((m) => moduleHasData(m))
    .map((m) => ({
      ...m,
      selections: (m.selections || []).filter((s) => s.optionId),
    }));
}

export function flattenAssessmentCatalog(catalog) {
  const list = [];
  for (const cat of catalog || []) {
    for (const mod of cat.modules || []) {
      list.push({
        ...mod,
        categoryName: cat.name,
        categoryCode: cat.code,
      });
    }
  }
  return list;
}

export default function StudentAssessmentEditor({
  catalog,
  modules,
  overallRemarks,
  readOnly,
  onChangeSelection,
  onChangeModuleRemarks,
  onChangeSendCustomNotesOnly,
  onChangeOverallRemarks,
}) {
  const flatModules = useMemo(() => flattenAssessmentCatalog(catalog), [catalog]);
  const tableSections = useMemo(() => {
    const sections = [];
    let lastCat = null;
    for (const mod of flatModules) {
      if (mod.categoryName !== lastCat) {
        sections.push({ type: 'category', key: mod.categoryCode || mod.categoryName, label: mod.categoryName });
        lastCat = mod.categoryName;
      }
      sections.push({ type: 'module', key: mod.id, mod });
    }
    return sections;
  }, [flatModules]);

  const getModuleState = (moduleId) => (modules || []).find((m) => m.moduleId === moduleId);

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 700, width: '28%' }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '32%' }}>Parameter</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '40%' }}>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableSections.map((section) => {
              if (section.type === 'category') {
                return (
                  <TableRow key={section.key}>
                    <TableCell
                      colSpan={3}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 700,
                        py: 1,
                      }}
                    >
                      {section.label}
                    </TableCell>
                  </TableRow>
                );
              }

              const mod = section.mod;
              const modState = getModuleState(mod.id);
              if (!modState) return null;
              const params = mod.parameters || [];

              return (
                <React.Fragment key={mod.id}>
                  {params.map((param, idx) => {
                    const sel = modState.selections.find((s) => s.parameterId === param.id);
                    return (
                      <TableRow key={param.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        {idx === 0 && (
                          <TableCell
                            rowSpan={params.length + 1}
                            sx={{
                              verticalAlign: 'top',
                              fontWeight: 600,
                              borderRight: 1,
                              borderColor: 'divider',
                            }}
                          >
                            {mod.name}
                          </TableCell>
                        )}
                        <TableCell>{param.name}</TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" disabled={readOnly}>
                            <Select
                              displayEmpty
                              value={sel?.optionId || EMPTY}
                              onChange={(e) => onChangeSelection(mod.id, param.id, e.target.value)}
                              renderValue={(v) => {
                                if (!v) return <Typography color="text.secondary">— Select —</Typography>;
                                const opt = param.options.find((o) => o.id === v);
                                return opt?.label || v;
                              }}
                            >
                              <MenuItem value={EMPTY}>
                                <em>Not assessed</em>
                              </MenuItem>
                              {(param.options || []).map((opt) => (
                                <MenuItem
                                  key={opt.id}
                                  value={opt.id}
                                  sx={{
                                    whiteSpace: 'normal',
                                    alignItems: 'flex-start',
                                    flexDirection: 'column',
                                    py: 1,
                                  }}
                                >
                                  <Typography variant="body2">{opt.label}</Typography>
                                  {opt.helpText ? (
                                    <Typography variant="caption" color="text.secondary">
                                      {opt.helpText}
                                    </Typography>
                                  ) : null}
                                </MenuItem>
                              ))}
                            </Select>
                            {(() => {
                              const selected = (param.options || []).find((o) => o.id === sel?.optionId);
                              return selected?.helpText ? (
                                <FormHelperText sx={{ mx: 0, mt: 0.5 }}>{selected.helpText}</FormHelperText>
                              ) : null;
                            })()}
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      Remarks (optional)
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Notes for this module"
                        value={modState.remarks || ''}
                        disabled={readOnly}
                        onChange={(e) => onChangeModuleRemarks(mod.id, e.target.value)}
                      />
                      {modState.remarks?.trim() ? (
                        <FormControlLabel
                          sx={{ mt: 0.5, ml: 0, alignItems: 'flex-start' }}
                          control={
                            <Checkbox
                              size="small"
                              checked={Boolean(modState.sendCustomNotesOnly)}
                              disabled={readOnly}
                              onChange={(e) => onChangeSendCustomNotesOnly(mod.id, e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="caption" color="text.secondary">
                              Send only my notes to parents (hide the default explanation)
                            </Typography>
                          }
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Overall remarks (optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="Any general comment for supervisor or parent"
          value={overallRemarks}
          disabled={readOnly}
          onChange={(e) => onChangeOverallRemarks(e.target.value)}
        />
      </Paper>
    </>
  );
}
